/**
 * Curated assistant-icon vocabulary.
 *
 * Operators give an assistant a semantic icon by setting `settings.icon` in
 * its YAML to one of the kebab-case keys below (e.g. `icon: scale`). The Hub
 * renders that Lucide line-icon on the brand gradient badge. Anything unset
 * or unrecognised falls back to the name monogram — so the grid always looks
 * complete with zero per-assistant work, and gets sharper as operators opt in.
 *
 * The set is intentionally small and curated (not "any of Lucide's 1000+")
 * so the vocabulary stays consistent across tenants and the bundle only pulls
 * the icons we actually offer. Add a row here to extend it.
 */
import type { Component } from "vue";
import {
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  CalendarDays,
  Clock,
  Database,
  FilePen,
  FilePlus,
  FileSearch,
  FileText,
  FlaskConical,
  Globe,
  GraduationCap,
  HeartPulse,
  Landmark,
  Lightbulb,
  LifeBuoy,
  Mail,
  MessageSquare,
  Receipt,
  Scale,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  UserSearch,
  Wrench,
} from "lucide-vue-next";

/** kebab-case name (operator-facing) → Lucide component. */
export const ASSISTANT_ICONS: Record<string, Component> = {
  scale: Scale, // legal / compliance
  "file-text": FileText, // documents / policies
  "file-search": FileSearch, // contracts / document search
  "shield-check": ShieldCheck, // governance / security
  users: Users, // HR / people
  "user-search": UserSearch, // booking / matching consultants
  briefcase: Briefcase, // business / sales
  "book-open": BookOpen, // knowledge / handbook
  calendar: CalendarDays, // scheduling
  clock: Clock, // time logging (eregnskab)
  "bar-chart": BarChart3, // analytics / reporting
  database: Database, // data / SQL
  "message-square": MessageSquare, // general chat
  sparkles: Sparkles, // general / AI
  landmark: Landmark, // finance / public sector
  "graduation-cap": GraduationCap, // training / learning
  "heart-pulse": HeartPulse, // pharma / life science
  flask: FlaskConical, // lab / R&D
  building: Building2, // company / org
  mail: Mail, // email / statusmail
  receipt: Receipt, // invoices / billing
  wrench: Wrench, // ops / tooling
  globe: Globe, // web / external
  lightbulb: Lightbulb, // ideas / innovation
  // Added 2026-08-22. These three were already set in the assistant YAMLs —
  // delivery-economics, support-insight and proposal-generator — but were not
  // in this map, so `resolveAssistantIcon` returned null and all three fell
  // back to a name monogram with no error anywhere. The YAML authors picked
  // better icons than anything the list then held; the list was too narrow.
  "trending-up": TrendingUp, // utilisation / economics trends
  "life-buoy": LifeBuoy, // support / service desk
  "file-plus": FilePlus, // drafting a new document
  // Added 2026-09-26: delivery-agreements (SOW) set `file-signature` and fell
  // back to the monogram, the same silent miss as above. Lucide renamed
  // FileSignature to FilePen, so the operator-facing name maps to that.
  // tests/test_assistant_icons.py now fails CI on any YAML icon not listed.
  "file-signature": FilePen, // agreements / SOW / signing
};

/** The list of valid names — handy for docs / an admin picker later. */
export const ASSISTANT_ICON_NAMES = Object.keys(ASSISTANT_ICONS);

/**
 * Built-in icon defaults for the shipped assistants, keyed on slug. These
 * let the semantic icons show on a frontend deploy *alone* — before the
 * backend reseeds `settings.icon` from YAML. An operator-set `settings.icon`
 * always takes precedence (see the Hub's `iconOf`); slugs not listed here
 * just fall back to the name monogram. Keep this in sync with the YAML
 * `settings.icon` values so the two never disagree.
 */
export const DEFAULT_ASSISTANT_ICONS: Record<string, string> = {
  "ai-governance": "shield-check",
  "booking-matcher": "user-search",
  "contract-intelligence": "file-search",
  "cv-builder": "file-text",
  "delivery-agreements": "file-signature",
  default: "sparkles",
  eregnskab: "clock",
};

/**
 * Resolve an operator-supplied `settings.icon` value to a component.
 * Returns null for unset / unknown names so the caller can fall back to the
 * monogram. Tolerant of casing and stray whitespace.
 */
export function resolveAssistantIcon(name: unknown): Component | null {
  if (typeof name !== "string") return null;
  const key = name.trim().toLowerCase();
  return ASSISTANT_ICONS[key] ?? null;
}
